import type { Lang, SiteCopy } from '../i18n/types';
import {
  UB_HERO_PIPELINE_SVG,
  ubAnalyticsPanel,
  ubCatalogPanel,
  ubContrastNowSvg,
  ubContrastPastSvg,
  ubIngestPanel,
  ubOrgPanel,
  ubSimPanel,
  ubStoragePanel,
} from '../illustrations/unite-base';

export const UNITE_BASE_PATH_EN = '/solutions/unite-base';
export const UNITE_BASE_PATH_ZH = '/zh/solutions/unite-base';

export function uniteBasePath(lang: Lang): string {
  return lang === 'zh' ? UNITE_BASE_PATH_ZH : UNITE_BASE_PATH_EN;
}

export const UNITE_BASE_META = {
  zh: {
    title: 'UniteBase · 具身智能一体化数据底座',
    description:
      '告别散乱文件与零碎脚本。UniteBase 以 S3 为底层存储，覆盖数据接入、数据集管理与 SQL / DataFrame 分析，让机器人海量数据转化为可持续的团队资产。',
    keywords: 'UniteBase,具身智能,数据平台,SQL,DataFrame,数据接入,数据集管理,数据分析,AI Copilot,UniteStream',
  },
  en: {
    title: 'UniteBase · Unified embodied data foundation',
    description:
      'Stop juggling scripts and scattered files. UniteBase is built on S3 with unified ingest, dataset catalog, and SQL / DataFrame analytics so robot data becomes durable team assets.',
    keywords: 'UniteBase,embodied AI,data platform,SQL,DataFrame,data ingest,dataset catalog,analytics,AI Copilot,UniteStream',
  },
} as const;

type ContrastItem = { text: string };
type Pillar = { title: string; body: string; panel: () => string };
type Extension = { title: string; body: string; panel: () => string };

type Copy = {
  heroTitle: string;
  heroSubtitle: string;
  ctaPrimary: string;
  ctaSecondary: string;
  contrastPastLabel: string;
  contrastPastTitle: string;
  contrastPastItems: ContrastItem[];
  contrastNowLabel: string;
  contrastNowTitle: string;
  contrastNowItems: ContrastItem[];
  pillarsEyebrow: string;
  pillarsTitle: string;
  pillars: Pillar[];
  extensionsEyebrow: string;
  extensionsTitle: string;
  extensions: Extension[];
  closingTitle: string;
  closingSubtitle: string;
  closingCta: string;
};

const COPY: Record<Lang, Copy> = {
  zh: {
    heroTitle: '具身智能的一体化数据底座。',
    heroSubtitle:
      '告别散乱文件与零碎脚本。从数据接入、存储、管理到分析，UniteBase 让机器人海量数据真正转化为可持续的团队资产。',
    ctaPrimary: '开启现代化数据管线',
    ctaSecondary: '查看完整文档',
    contrastPastLabel: '过去',
    contrastPastTitle: '格式各异、脚本复杂、文件散落、协作断层',
    contrastPastItems: [
      { text: '各项目自建脚本，格式与字段无法对齐。' },
      { text: '数据分散在各存储系统，血缘与权限无从追溯。' },
      { text: '算法与工程各自为战，协作成本居高不下。' },
    ],
    contrastNowLabel: 'UniteBase',
    contrastNowTitle: '统一规范、S3 底层存储、全局目录、SQL 与 DataFrame 分析',
    contrastNowItems: [
      { text: '平台级接入规范，兼容对接各类数据源与存储系统。' },
      { text: '数据持久化于 S3，并与设备、任务、组织上下文绑定。' },
      { text: '全局数据集目录，SQL 探查与多模态 DataFrame 批处理开箱即用。' },
    ],
    pillarsEyebrow: '核心四步',
    pillarsTitle: '四步贯通的数据闭环',
    pillars: [
      {
        title: '数据接入：规范源头，自动化采集',
        body: '在平台登记设备与数据源（兼容对接各类存储系统），创建自动化采集任务，只让符合业务规则的数据进入统一管线，从源头消除格式与字段混乱。',
        panel: ubIngestPanel,
      },
      {
        title: '数据存储：S3 对象存储，上下文绑定',
        body: '底层采用 S3 对象存储持久化接入与分析产出，并与来源设备、任务及组织架构绑定，确保上下文清晰、权限隔离。',
        panel: ubStoragePanel,
      },
      {
        title: '数据集管理：可视资产，全局检索',
        body: '打破团队孤岛。所有数据转化为统一的「数据集」资产，支持全局检索、表结构透视与分页预览，让算法与研发同学随时调用。',
        panel: ubCatalogPanel,
      },
      {
        title: '数据分析：SQL 与 DataFrame 双引擎',
        body: '让数据持续产生价值。SQL 适合联调探查与即席查询，DataFrame 承接大规模批处理与复杂变换，原生支持图像、视频、音频与轨迹等多模态列类型，可与 episode 元数据同表分析。内置 AI Copilot 辅助生成逻辑，产出自动回写为新数据集。',
        panel: ubAnalyticsPanel,
      },
    ],
    extensionsEyebrow: '扩展能力',
    extensionsTitle: '仿真验证与组织治理',
    extensions: [
      {
        title: '3D 仿真验证',
        body: '数据对不对，跑一下就知道。支持在云端三维场景中直接回放，直观验证采集数据与机器人物理动作的一致性。',
        panel: ubSimPanel,
      },
      {
        title: '资源与组织中枢',
        body: '集中管理相关模型与配置文件，提供全局组织视图与细粒度的团队成员基础权限控制。',
        panel: ubOrgPanel,
      },
    ],
    closingTitle: '让具身智能的数据流转，从未如此顺畅。',
    closingSubtitle: '平滑升级你的团队协作体系，从分散的脚本迈向现代化的数据基建。',
    closingCta: '立即体验 UniteBase 控制台',
  },
  en: {
    heroTitle: 'The unified data foundation for embodied AI.',
    heroSubtitle:
      'Leave scattered files and one-off scripts behind. From ingest and storage to catalog and analytics, UniteBase turns robot-scale data into durable team assets.',
    ctaPrimary: 'Start the modern data pipeline',
    ctaSecondary: 'Read the docs',
    contrastPastLabel: 'Before',
    contrastPastTitle: 'Mixed formats, brittle scripts, siloed files',
    contrastPastItems: [
      { text: 'Every project ships its own scripts and schemas.' },
      { text: 'Data sprawls across storage systems — no lineage or access control.' },
      { text: 'Research and engineering work in isolation.' },
    ],
    contrastNowLabel: 'UniteBase',
    contrastNowTitle: 'One standard, S3 storage, catalog, SQL & DataFrame analytics',
    contrastNowItems: [
      { text: 'Platform ingest rules — compatible with diverse sources and storage backends.' },
      { text: 'Data persists on S3, bound to devices, jobs, and org context.' },
      { text: 'Global dataset catalog with SQL exploration and multimodal DataFrame batch jobs built in.' },
    ],
    pillarsEyebrow: 'Four pillars',
    pillarsTitle: 'Four steps, one closed loop',
    pillars: [
      {
        title: 'Ingest: clean sources, automated capture',
        body: 'Register devices and sources (compatible with diverse storage backends), schedule capture jobs, and only admit rule-compliant data into the unified pipeline.',
        panel: ubIngestPanel,
      },
      {
        title: 'Storage: S3 object storage with full context',
        body: 'Built on S3 for durable persistence of ingest and analytics outputs — bound to source devices, jobs, and org structure for clear context and access isolation.',
        panel: ubStoragePanel,
      },
      {
        title: 'Catalog: searchable dataset assets',
        body: 'One dataset layer for the whole team — global search, schema inspection, and paginated previews on demand.',
        panel: ubCatalogPanel,
      },
      {
        title: 'Analytics: SQL and DataFrame at the core',
        body: 'SQL for ad-hoc queries and fast iteration; DataFrame for large-scale batch transforms with native multimodal column types — images, video, audio, and trajectories alongside episode metadata in one table. AI Copilot drafts logic for both, and outputs write back as new catalog datasets.',
        panel: ubAnalyticsPanel,
      },
    ],
    extensionsEyebrow: 'Extensions',
    extensionsTitle: 'Simulation validation and org governance',
    extensions: [
      {
        title: '3D simulation validation',
        body: 'Replay trajectories in a bright cloud 3D view and confirm motion matches the physical robot before data ships downstream.',
        panel: ubSimPanel,
      },
      {
        title: 'Org & resource hub',
        body: 'Centralize models, configs, and org-wide views with fine-grained member permissions.',
        panel: ubOrgPanel,
      },
    ],
    closingTitle: 'Embodied data has never flowed this smoothly.',
    closingSubtitle: 'Move from scattered scripts to a modern data foundation your whole team can trust.',
    closingCta: 'Open the UniteBase console',
  },
};

export function renderUniteBasePage(
  _siteCopy: SiteCopy,
  lang: Lang,
  esc: (s: string) => string,
): string {
  const c = COPY[lang];

  const pillarRows = c.pillars
    .map(
      (p, i) => `
      <article class="ub-pillar reveal${i === 0 ? ' is-active' : ''}${i % 2 ? ' ub-pillar--reverse' : ''}" data-pillar="${i}" style="transition-delay:${i * 60}ms">
        <div class="ub-pillar__copy">
          <h3>${esc(p.title)}</h3>
          <p>${esc(p.body)}</p>
        </div>
        <div class="ub-pillar__visual">
          ${p.panel()}
        </div>
      </article>`,
    )
    .join('');

  const extensionCards = c.extensions
    .map(
      (ex) => `
      <article class="ub-ext reveal">
        <div class="ub-ext__copy">
          <h3>${esc(ex.title)}</h3>
          <p>${esc(ex.body)}</p>
        </div>
        <div class="ub-ext__visual">${ex.panel()}</div>
      </article>`,
    )
    .join('');

  return `
    <section class="ub-hero" id="top">
      <div class="ub-hero__inner">
        <div class="ub-hero__pipeline reveal">${UB_HERO_PIPELINE_SVG}</div>
        <h1 class="ub-hero__title reveal">${esc(c.heroTitle)}</h1>
        <p class="ub-hero__subtitle reveal">${esc(c.heroSubtitle)}</p>
        <div class="ub-hero__actions reveal">
          <a class="ub-btn ub-btn--primary" href="#contact">${esc(c.ctaPrimary)}</a>
          <a class="ub-btn ub-btn--outline" href="#pillars">${esc(c.ctaSecondary)}</a>
        </div>
      </div>
    </section>

    <section class="ub-contrast" id="compare">
      <div class="ub-contrast__inner">
        <article class="ub-contrast__card ub-contrast__card--past reveal">
          ${ubContrastPastSvg()}
          <span class="ub-contrast__label">${esc(c.contrastPastLabel)}</span>
          <h2>${esc(c.contrastPastTitle)}</h2>
          <ul>
            ${c.contrastPastItems.map((item) => `<li>${esc(item.text)}</li>`).join('')}
          </ul>
        </article>
        <article class="ub-contrast__card ub-contrast__card--now reveal" style="transition-delay:80ms">
          ${ubContrastNowSvg()}
          <span class="ub-contrast__label">${esc(c.contrastNowLabel)}</span>
          <h2>${esc(c.contrastNowTitle)}</h2>
          <ul>
            ${c.contrastNowItems.map((item) => `<li>${esc(item.text)}</li>`).join('')}
          </ul>
        </article>
      </div>
    </section>

    <section class="ub-pillars section" id="pillars">
      <div class="section__inner ub-pillars__wrap">
        <header class="ub-pillars__head reveal">
          <p class="section__eyebrow">${esc(c.pillarsEyebrow)}</p>
          <h2 class="section__title section__title--center">${esc(c.pillarsTitle)}</h2>
        </header>
        <div class="ub-pillars__layout">
          <div class="ub-pillars__line" aria-hidden="true">
            <span class="ub-pillars__line-fill"></span>
          </div>
          <div class="ub-pillars__list">
            ${pillarRows}
          </div>
        </div>
      </div>
    </section>

    <section class="ub-ext section" id="extensions">
      <div class="section__inner">
        <header class="ub-ext__head reveal">
          <p class="section__eyebrow">${esc(c.extensionsEyebrow)}</p>
          <h2 class="section__title section__title--center">${esc(c.extensionsTitle)}</h2>
        </header>
        <div class="ub-ext__grid">
          ${extensionCards}
        </div>
      </div>
    </section>

    <section class="ub-closing section" id="closing">
      <div class="section__inner">
        <div class="ub-closing__panel reveal">
          <h2 class="ub-closing__title">${esc(c.closingTitle)}</h2>
          <p class="ub-closing__subtitle">${esc(c.closingSubtitle)}</p>
          <div class="ub-closing__actions">
            <a class="ub-btn ub-btn--primary ub-btn--lg" href="#contact">${esc(c.closingCta)}</a>
          </div>
        </div>
      </div>
    </section>
  `;
}
